import { Link } from "@tanstack/react-router";
import { useAuthStore } from "../auth/store.ts";

export function LandingPage() {
  const user = useAuthStore((s) => s.user);

  return (
    <div className="landing-page">
      {/* Background ambient lighting */}
      <div className="landing-ambient">
        <div className="landing-ambient-blob b1" />
        <div className="landing-ambient-blob b2" />
        <div className="landing-ambient-blob b3" />
        <div className="landing-grid-bg" />
      </div>

      {/* Navigation */}
      <nav className="landing-nav">
        <Link to="/" className="landing-brand">
          <div className="landing-logo-box">
            <svg width="22" height="22" viewBox="0 0 40 40" fill="none">
              <path
                d="M12 15C12 12.2386 14.2386 10 17 10H24C26.7614 10 29 12.2386 29 15C29 17.7614 26.7614 20 24 20H16C13.2386 20 11 22.2386 11 25C11 27.7614 13.2386 30 16 30H23C25.7614 30 28 27.7614 28 25"
                stroke="var(--primary)"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx="27" cy="15" r="3" fill="var(--primary)" />
              <circle cx="13" cy="25" r="3" fill="#6366f1" />
            </svg>
          </div>
          <span className="landing-brand-title">SellSynq</span>
        </Link>

        <div className="landing-nav-links">
          <a href="#features" className="landing-nav-link">Conversation Intelligence</a>
          <a href="#deals" className="landing-nav-link">Deal Execution</a>
          <a href="#coaching" className="landing-nav-link">Live Copilot</a>
          <a href="#metrics" className="landing-nav-link">ROI</a>
        </div>

        <div className="landing-nav-actions">
          {user ? (
            <Link to="/dashboard" className="landing-btn-primary">
              Go to Dashboard →
            </Link>
          ) : (
            <>
              <Link to="/signin" className="landing-btn-secondary">
                Sign In
              </Link>
              <Link to="/signin" className="landing-btn-primary">
                Launch Copilot
              </Link>
            </>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <header className="landing-hero">
        <div className="landing-badge">
          <span className="landing-badge-dot" />
          <span>SellSynq — Your Invisible AI Sales Assistant</span>
        </div>

        <h1 className="landing-h1">
          Turn Every Sales Conversation Into{" "}
          <span className="landing-h1-gradient">Closed Revenue</span>
        </h1>

        <p className="landing-sub">
          SellSynq listens live during enterprise sales calls, whispers real-time objection handling &
          battlecards, detects deal risks before they stall, and writes your CRM recap in seconds.
        </p>

        <div className="landing-hero-actions">
          <Link to="/signin" className="landing-btn-primary lg">
            Start Live Call Copilot →
          </Link>
          <a href="#demo" className="landing-btn-secondary lg">
            Explore Interactive Demo
          </a>
        </div>

        {/* Gong-Style Live Call Center Showcase */}
        <div className="landing-mockup-wrap" id="demo">
          <div className="landing-mockup">
            {/* Window Topbar */}
            <div className="landing-mockup-top">
              <div className="landing-mockup-dots">
                <span className="landing-mockup-dot r" />
                <span className="landing-mockup-dot y" />
                <span className="landing-mockup-dot g" />
              </div>
              <div className="landing-mockup-header-info">
                <span className="landing-live-tag">
                  <span className="landing-live-tag-dot" /> Live Call
                </span>
                <span>Acme Corp · Enterprise Platform Modernization</span>
                <span style={{ color: "#64748b" }}>|</span>
                <span style={{ fontFamily: "var(--font-mono)", color: "#94a3b8" }}>24:18</span>
              </div>
              <div style={{ fontSize: "12px", color: "#94a3b8" }}>
                SellSynq Copilot v2.4 Active
              </div>
            </div>

            {/* 3-Column Showcase Body */}
            <div className="landing-mockup-body">
              {/* Left Column: Deal Health & Talk Ratio */}
              <div className="mockup-rail-left">
                <div>
                  <div className="rail-label">Talk vs Listen Ratio</div>
                  <div className="talk-ratio-box">
                    <div className="talk-ratio-legend">
                      <span style={{ color: "var(--primary)", fontWeight: 600 }}>Rep (42%)</span>
                      <span style={{ color: "#64748B", fontWeight: 600 }}>Buyer (58%)</span>
                    </div>
                    <div className="talk-ratio-meter">
                      <div className="talk-ratio-bar-rep" style={{ width: "42%" }} />
                      <div className="talk-ratio-bar-prospect" style={{ width: "58%" }} />
                    </div>
                    <div style={{ fontSize: "11px", color: "#10b981", fontWeight: 600 }}>
                      ✓ Optimal Discovery Balance
                    </div>
                  </div>
                </div>

                <div>
                  <div className="rail-label">Deal Health Radar</div>
                  <div className="deal-radar-box">
                    <div className="deal-radar-score">
                      <span className="deal-score-num">89</span>
                      <span style={{ fontSize: "14px", color: "#64748b" }}>/ 100</span>
                      <span className="deal-radar-tag">High Momentum</span>
                    </div>
                    <div style={{ fontSize: "12px", color: "#64748b", marginTop: "8px" }}>
                      Decision Maker identified · Security requirements validated
                    </div>
                  </div>
                </div>

                <div>
                  <div className="rail-label">Active Competitors Detected</div>
                  <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                    <span style={{ fontSize: "11.5px", background: "rgba(239,68,68,0.1)", color: "#dc2626", padding: "3px 8px", borderRadius: "6px", border: "1px solid rgba(239,68,68,0.2)" }}>
                      Snowflake · Pricing Push
                    </span>
                    <span style={{ fontSize: "11.5px", background: "rgba(79,70,229,0.08)", color: "var(--primary)", padding: "3px 8px", borderRadius: "6px" }}>
                      AWS Redshift
                    </span>
                  </div>
                </div>
              </div>

              {/* Center Column: Live Transcript & Waveform */}
              <div className="mockup-center">
                {/* Simulated Audio Wave */}
                <div className="audio-waveform-container">
                  <span style={{ fontSize: "11px", color: "var(--primary)", fontWeight: 600, marginRight: "8px" }}>LIVE AUDIO</span>
                  {[24, 40, 16, 52, 34, 18, 64, 42, 28, 56, 32, 20, 48, 60, 38, 22, 50, 36, 18, 44, 30, 60, 42, 24].map((h, i) => (
                    <div
                      key={i}
                      className="audio-bar"
                      style={{
                        height: `${h}%`,
                        opacity: i > 18 ? 0.35 : 0.85,
                        background: "var(--primary)"
                      }}
                    />
                  ))}
                </div>

                {/* Transcript Message 1 */}
                <div className="transcript-message">
                  <div className="speaker-avatar prospect">VP</div>
                  <div className="transcript-bubble">
                    <div className="transcript-name">Danielle Ross (VP Engineering · Acme)</div>
                    <div>
                      We really like the architecture, but our finance team is pushing back.
                      <span className="transcript-highlight">
                        "Your annual contract value is 25% higher than what Snowflake quoted us for migration."
                      </span>
                      How can we justify this to our CFO?
                    </div>
                  </div>
                </div>

                {/* Transcript Message 2 */}
                <div className="transcript-message">
                  <div className="speaker-avatar rep">AE</div>
                  <div className="transcript-bubble">
                    <div className="transcript-name">Alex Miller (Senior AE · SellSynq)</div>
                    <div>
                      Completely understand, Danielle. When factoring in the automated ETL overhead and query concurrency licensing, our clients typically see a 38% total cost of ownership reduction by month 6...
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column: SellSynq AI Whisper Rail */}
              <div className="mockup-rail-right">
                <div className="rail-label">SellSynq AI Whisper · Real-Time</div>

                <div className="ai-whisper-card">
                  <div className="ai-whisper-head">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                    <span>Objection Battlecard Triggered</span>
                  </div>
                  <div className="ai-whisper-content">
                    Highlight that Snowflake charges per virtual warehouse size during compute surges. Reference <strong>FinTech Global Case Study</strong> where they saved $380,000 in Year 1.
                  </div>
                  <div className="whisper-stat">
                    ⚡ 87% win-rate when using this counter-point
                  </div>
                </div>

                <div className="ai-whisper-card">
                  <div className="ai-whisper-head">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 14 14"/></svg>
                    <span>Suggested Next Action</span>
                  </div>
                  <div className="ai-whisper-content">
                    Offer a joint TCO modeling workshop with their Director of FinOps before Thursday's budget review.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Social Proof / Metrics Bar */}
      <section className="landing-metrics-bar" id="metrics">
        <div>
          <div className="metric-number">48%</div>
          <div className="metric-label">Higher Win Rate on Competitive Deals</div>
        </div>
        <div>
          <div className="metric-number">3.2x</div>
          <div className="metric-label">Faster New Rep Quota Ramp Time</div>
        </div>
        <div>
          <div className="metric-number">100%</div>
          <div className="metric-label">Automated CRM Hygiene & Action Sync</div>
        </div>
        <div>
          <div className="metric-number">$1.4M</div>
          <div className="metric-label">Average Annual Revenue Velocity Lift</div>
        </div>
      </section>

      {/* 3 Core Pillars */}
      <section className="landing-section" id="features">
        <div className="section-header">
          <div className="section-tag">Enterprise Revenue Platform</div>
          <h2 className="section-title">
            The Three Pillars of Winning Revenue Teams
          </h2>
          <p style={{ color: "#94a3b8", fontSize: "16px", marginTop: "12px" }}>
            From pre-call preparation to live conversational whispers and autonomous deal execution.
          </p>
        </div>

        <div className="bento-grid">
          <div className="bento-card featured">
            <div className="bento-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
            </div>
            <h3 className="bento-title">Live In-Call Whisper & Objection Defense</h3>
            <p className="bento-desc">
              Never get caught off-guard on pricing, architecture, or competitors again. SellSynq analyzes
              spoken words in real-time and whispers instant responses, ROI metrics, and proven battlecards
              directly to your screen as the customer speaks.
            </p>
            <div style={{ marginTop: "24px", display: "flex", gap: "12px", flexWrap: "wrap" }}>
              <span style={{ fontSize: "12px", background: "rgba(79,70,229,0.08)", color: "var(--primary)", padding: "4px 10px", borderRadius: "999px" }}>
                ⚡ Sub-second Latency
              </span>
              <span style={{ fontSize: "12px", background: "#F1F5F9", color: "#475569", padding: "4px 10px", borderRadius: "999px" }}>
                Competitive Intelligence
              </span>
              <span style={{ fontSize: "12px", background: "#F1F5F9", color: "#475569", padding: "4px 10px", borderRadius: "999px" }}>
                Live Talk-Ratio Alerts
              </span>
            </div>
          </div>

          <div className="bento-card">
            <div className="bento-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
            </div>
            <h3 className="bento-title">Deal Risk & Pipeline Radar</h3>
            <p className="bento-desc">
              Spot ghosting risks, single-threaded accounts, and stalled momentum weeks before quarter end.
              Every deal receives a continuous health score backed by real customer sentiment.
            </p>
          </div>

          <div className="bento-card">
            <div className="bento-icon" style={{ color: "#10b981", borderColor: "rgba(16,185,129,0.2)", background: "rgba(16,185,129,0.08)" }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
            </div>
            <h3 className="bento-title">Autonomous Deal Briefs</h3>
            <p className="bento-desc">
              Zero manual note-taking. Seconds after hanging up, SellSynq produces executive summaries, action
              item matrix with owners and deadlines, and customer-ready follow-up emails.
            </p>
          </div>

          <div className="bento-card featured">
            <div className="bento-icon" style={{ color: "#d97706", borderColor: "rgba(217,119,6,0.2)", background: "rgba(217,119,6,0.08)" }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            </div>
            <h3 className="bento-title">Ask SellSynq AI · Cross-Call Intelligence</h3>
            <p className="bento-desc">
              Query your entire sales history in plain English: "Which enterprise customers cited budget freezing in Q3?",
              "What questions did the CTO of FinTech Corp ask about SSO?", or "Generate a comparison matrix across our top 5 closed-won accounts".
            </p>
          </div>
        </div>
      </section>

      {/* CTA Footer Banner */}
      <section className="landing-cta-banner">
        <h2 className="landing-cta-h2">Ready to Transform Your Sales Execution?</h2>
        <p className="landing-cta-sub">
          Equip your revenue team with the AI copilot that guides every conversation to victory.
        </p>
        <Link to="/signin" className="landing-btn-primary lg">
          Launch SellSynq Copilot Now →
        </Link>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={{ fontWeight: 700, color: "#0F172A" }}>SellSynq</span>
          <span>© 2026 SellSynq — Your Invisible AI Sales Assistant.</span>
        </div>
        <div style={{ display: "flex", gap: "20px" }}>
          <Link to="/signin" style={{ color: "#64748B", textDecoration: "none" }}>Sign In</Link>
          <a href="#demo" style={{ color: "#64748B", textDecoration: "none" }}>Interactive Demo</a>
          <a href="mailto:support@sellsynq.com" style={{ color: "#64748B", textDecoration: "none" }}>Contact Support</a>
        </div>
      </footer>
    </div>
  );
}
